import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager, type Color, type NoColor } from "@salestrak/ui";

import { UpdateUserDocument } from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";

// One hook behind the avatar-edit popover (Color palette + Upload / Remove):
// the photo pick+crop flow rides the existing media upload pipeline at the
// call site; color and photo-removal are plain updateUser patches. Works on
// any target user — self-service and cross-user admin edits share the
// server-side gate.
export function useAvatarEditor(userId: string) {
  const [updateUserMutation] = useMutation(UpdateUserDocument);

  const patch = useCallback(
    async (input: { avatarColor?: string | null; profileImage?: null }) => {
      try {
        const { data } = await updateUserMutation({
          variables: { id: userId, input },
        });
        const result = data?.updateUser;
        // errorPolicy "all": a rejected mutation RESOLVES with no data (the
        // error link has already toasted the denial) — success may only be
        // announced when the server actually returned the updated user.
        if (!result) return;
        if ("code" in result) {
          toastManager.add({
            title: result.message ?? "Failed to update avatar",
            type: "error",
          });
          return;
        }
        trackEvent({
          context: AnalyticsContext.Settings,
          event: SettingsEvent.Update,
          label: "avatar",
          data: { change: "profileImage" in input ? "remove_photo" : "color" },
        });
      } catch (err) {
        toastMutationError(err, "Failed to update avatar");
      }
    },
    [updateUserMutation, userId],
  );

  const setColor = useCallback(
    (color: Color | NoColor) => patch({ avatarColor: color }),
    [patch],
  );

  const removePhoto = useCallback(() => patch({ profileImage: null }), [patch]);

  return { setColor, removePhoto };
}
